"use client";

import React from "react";
import { Wind, Cuboid, Scale, FileCode, Cpu, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  title: "Infrastructure",
  icon: Cuboid,
  description: "Developing the backbone of modern digital commerce, focusing on scalable architecture and secure digital plumbing for global holdings like Cannabis Cloud.",
  codeSnippet: "contract.validate(eagle_id);"
}, {
  title: "Smart Contracting",
  icon: Scale,
  description: "Leveraging blockchain and automated contracting to unify portfolio management and ensure transparent, trustless deal-making across digital infrastructure.",
  codeSnippet: "const asset = new Holding('SaaS');"
}, {
  title: "Software Ventures",
  icon: Cpu,
  description: "Incubating high-potential software brands through rigorous strategic oversight, technical excellence, and the expansion of scalable intellectual property.",
  codeSnippet: "await requestAccess('eagle_47');"
}];
const backgroundSnippets = [{
  text: "contract.deploy(0x47)",
  top: "10%",
  left: "5%"
}, {
  text: "SHA-256: 8f2...9c",
  top: "15%",
  right: "10%"
}, {
  text: "import { holdings } from '@eagle'",
  bottom: "15%",
  left: "8%"
}, {
  text: "vault.lock(99.9)",
  bottom: "25%",
  right: "5%"
}, {
  text: "STATUS: ENCRYPTED",
  top: "50%",
  left: "50%",
  className: "opacity-5"
}];
export default function RaceEngineeringCore() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="66200" id="future-ventures" ref={ref} className="dark bg-background text-foreground relative overflow-hidden py-24 px-4 md:px-8">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden select-none">
        {backgroundSnippets.map((snippet, i) => <div key={i} data-index={i} className={cn("font-mono text-primary/10 absolute text-sm md:text-base whitespace-nowrap", snippet.className)} style={{
        top: snippet.top,
        left: snippet.left,
        right: snippet.right,
        bottom: snippet.bottom
      }}>
            {snippet.text}
          </div>)}
        
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-primary)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.03]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mb-16 text-center">
          <h2 className="font-default text-primary mb-4 text-4xl font-bold tracking-tighter uppercase md:text-5xl">
            THE LAB: EMERGING SYSTEMS
          </h2>
          <p className="font-sans text-muted-foreground mx-auto max-w-2xl text-lg">
            Strategic incubation of high-authority digital infrastructure and modern software ventures.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {pillars.map((pillar, i) => <div key={i} data-index={i} className={cn("group bg-card/40 border-primary/10 hover:border-primary/40 relative flex flex-col overflow-hidden rounded-sm border backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_0_30px_-10px_var(--color-primary)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              {}
              <div className="bg-primary/20 h-[1px] w-full">
                <div className="bg-primary h-full transition-all duration-1500 ease-out" style={{
              width: isVisible ? "100%" : "0%"
            }} />
              </div>

              <div className="flex flex-1 flex-col p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div className="bg-primary/10 text-primary rounded-sm p-3">
                    <pillar.icon className="h-8 w-8" strokeWidth={1.5} />
                  </div>
                  <div className="font-mono text-primary/40 text-xs">
                    SYSTEM_0{i + 1}
                  </div>
                </div>

                <h3 className="font-default text-primary mb-4 text-2xl font-semibold tracking-wide">
                  {pillar.title}
                </h3>

                <p className="font-sans text-muted-foreground mb-6 flex-1 leading-relaxed blur-[0.5px] group-hover:blur-none transition-all duration-500">
                  {pillar.description}
                </p>

                {}
                <div className="bg-background/40 border-primary/10 mt-auto rounded-sm border p-3">
                  <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-2">
                    <FileCode className="text-primary/60 h-3 w-3" />
                    <span className="font-mono text-muted-foreground text-[10px] uppercase tracking-widest">
                      LAB_LOG_0x47
                    </span>
                  </div>
                  <code className="font-mono text-primary/60 block text-xs">
                    {pillar.codeSnippet}
                  </code>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}